<template>
  <div class="cookie-consent print:hidden">
    <!-- A modal dialog, like the banner of the Business Navigator: the page waits for the visitor to choose -->
    <dialog
      ref="dialog"
      class="cookie-consent__dialog"
      :aria-labelledby="titleId"
      :aria-describedby="descriptionId"
      @cancel="onCancel"
    >
      <h2 :id="titleId" class="cookie-consent__title">{{ $t("shared.cookie_consent.title") }}</h2>

      <p :id="descriptionId" class="cookie-consent__text">
        {{ $t("shared.cookie_consent.description") }}
        <a v-if="privacyPolicyUrl" :href="privacyPolicyUrl" target="_blank" rel="noopener" class="cookie-consent__link">
          {{ $t("shared.cookie_consent.privacy_policy") }}
        </a>
      </p>

      <ul v-if="customising" class="cookie-consent__categories">
        <li v-for="category in categories" :key="category.name" class="cookie-consent__category">
          <VcSwitch
            :model-value="category.required || selection[category.name as OptionalCookieCategoryType]"
            :disabled="category.required"
            label-position="right"
            class="cookie-consent__switch"
            @update:model-value="select(category.name, $event)"
          >
            {{ $t(`shared.cookie_consent.categories.${category.name}.title`) }}
            <span v-if="category.required" class="cookie-consent__always">
              {{ $t("shared.cookie_consent.always_active") }}
            </span>
          </VcSwitch>

          <p class="cookie-consent__category-text">
            {{ $t(`shared.cookie_consent.categories.${category.name}.description`) }}
          </p>

          <details class="cookie-consent__details">
            <summary class="cookie-consent__summary">{{ $t("shared.cookie_consent.cookies_list") }}</summary>

            <dl v-for="cookie in category.cookies" :key="cookie.name" class="cookie-consent__cookie">
              <dt>
                <code>{{ cookie.name }}</code>
              </dt>
              <dd>{{ $t(`shared.cookie_consent.cookies.${cookie.textKey}.description`) }}</dd>
              <dd>
                {{ $t("shared.cookie_consent.duration") }}:
                {{ $t(`shared.cookie_consent.cookies.${cookie.textKey}.duration`) }}
              </dd>
            </dl>
          </details>
        </li>
      </ul>

      <div class="cookie-consent__actions">
        <button v-if="!customising" type="button" class="cookie-consent__button" @click="customise">
          {{ $t("shared.cookie_consent.customise") }}
        </button>
        <button v-else type="button" class="cookie-consent__button" @click="save(selection.analytics)">
          {{ $t("shared.cookie_consent.save_selection") }}
        </button>

        <button type="button" class="cookie-consent__button" @click="rejectOptional">
          {{ $t("shared.cookie_consent.reject_all") }}
        </button>

        <button type="button" class="cookie-consent__button" @click="acceptAll">
          {{ $t("shared.cookie_consent.accept_all") }}
        </button>
      </div>

      <button
        v-if="hasChosen"
        type="button"
        class="cookie-consent__close"
        :aria-label="$t('common.buttons.close')"
        @click="closeBanner"
      >
        <VcIcon name="x" size="sm" />
      </button>
    </dialog>

    <button
      v-if="!isBannerOpen"
      type="button"
      class="cookie-consent__reopen"
      :title="$t('shared.cookie_consent.settings')"
      :aria-label="$t('shared.cookie_consent.settings')"
      @click="openBanner"
    >
      <VcIcon name="finger-print" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { getCurrentInstance, onMounted, reactive, ref, watch } from "vue";
import { DEPLOYAI_URL_PRIVACY_POLICY } from "@/core/constants";
import { useCookieConsent } from "../composables/useCookieConsent";
import { CONSENT_COOKIE_NAME } from "../constants";
import type { CookieConsentType, OptionalCookieCategoryType } from "../types";

interface ICookieCategory {
  name: keyof CookieConsentType;
  required: boolean;
  /** Cookies listed to the visitor, with the key of their texts under `shared.cookie_consent.cookies` */
  cookies: { name: string; textKey: string }[];
}

const categories: ICookieCategory[] = [
  { name: "strictly_necessary", required: true, cookies: [{ name: CONSENT_COOKIE_NAME, textKey: "consent" }] },
  {
    name: "analytics",
    required: false,
    cookies: [
      { name: "_ga", textKey: "ga" },
      { name: "_ga_*", textKey: "ga_session" },
    ],
  },
];

const { consent, hasChosen, isBannerOpen, acceptAll, rejectOptional, save, openBanner, closeBanner } =
  useCookieConsent();

const uid = getCurrentInstance()!.uid;
const titleId = `cookie-consent-title-${uid}`;
const descriptionId = `cookie-consent-description-${uid}`;
const privacyPolicyUrl = DEPLOYAI_URL_PRIVACY_POLICY;

/** Showing the categories, to allow some of them only */
const customising = ref(false);
const selection = reactive<Record<OptionalCookieCategoryType, boolean>>({ analytics: false });

function customise() {
  selection.analytics = consent.value?.analytics ?? false;
  customising.value = true;
}

function select(name: keyof CookieConsentType, value: boolean | undefined) {
  if (name !== "strictly_necessary") {
    selection[name] = !!value;
  }
}

const dialog = ref<HTMLDialogElement | null>(null);

/** Escape closes the banner only once the visitor has chosen */
function onCancel(event: Event) {
  event.preventDefault();
  closeBanner();
}

function showBanner(open: boolean) {
  if (open) {
    // Reopened from the button: the categories come back folded
    customising.value = false;
    dialog.value?.showModal();
  } else {
    dialog.value?.close();
  }
}

onMounted(() => showBanner(isBannerOpen.value));
watch(isBannerOpen, showBanner);
</script>

<style lang="scss">
.cookie-consent {
  &__dialog {
    @apply m-auto max-h-[90vh] w-[90%] max-w-[700px] overflow-y-auto rounded-md border border-neutral-200 bg-additional-50 p-6 text-base leading-relaxed text-neutral-900 shadow-md md:w-3/5 xl:w-[45%];

    &[open] {
      @apply flex flex-col gap-4;
    }

    &::backdrop {
      background: rgb(0 0 0 / 30%);
    }
  }

  &__title {
    @apply pe-6 text-xl font-bold text-secondary;
  }

  &__text {
    @apply text-sm;
  }

  &__link {
    @apply text-secondary underline;

    &:hover {
      @apply text-secondary-700;
    }
  }

  &__categories {
    @apply flex flex-col divide-y divide-neutral-200 rounded border border-neutral-200;
  }

  &__category {
    @apply flex flex-col gap-1 px-4 py-3;
  }

  &__switch {
    @apply font-semibold;
  }

  &__always {
    @apply ms-1 text-sm font-normal text-secondary;
  }

  &__category-text {
    @apply text-sm text-neutral-700;
  }

  &__summary {
    @apply cursor-pointer text-sm font-bold text-secondary;
  }

  &__cookie {
    @apply mt-2 border-b border-neutral-100 pb-2 text-sm text-neutral-700 last:border-b-0;
  }

  // Three buttons of the same width, centered, stacked on small screens; the colors of the Business Navigator
  &__actions {
    @apply grid gap-2 text-center lg:grid-cols-3;
  }

  &__button {
    @apply w-full whitespace-nowrap rounded-sm border-2 border-secondary bg-additional-50 p-2 text-sm font-medium text-secondary shadow-[0_0_0.25em_rgba(0,0,0,0.2)] transition-colors;

    &:hover {
      @apply bg-secondary text-additional-50 shadow-lg;
    }

    &:focus-visible {
      @apply bg-primary outline-none;

      color: #000;
    }
  }

  &__close {
    @apply absolute end-4 top-4 text-neutral-500;

    &:hover {
      @apply text-secondary;
    }
  }

  &__reopen {
    @apply fixed bottom-7 start-7 z-[60] flex size-12 items-center justify-center rounded-2xl border-2 border-secondary bg-additional-50 p-2 text-secondary shadow-md transition-colors;

    // Like the buttons of the banner
    &:hover {
      @apply bg-secondary text-additional-50;
    }

    &:focus-visible {
      @apply bg-primary outline-none;

      color: #000;
    }
  }
}

// The page behind the banner does not scroll
body:has(.cookie-consent__dialog[open]) {
  @apply overflow-hidden;
}
</style>
